import { createSlice } from "@reduxjs/toolkit";
import { baseURL } from "../settings.js/dev";
import api from "../utils/api";
import { updateFlexTrack } from "./cart";
import { recaptchaAssessment } from "../utils/recaptcha";

const isRecaptchaError = (error) => {
    const msg = error?.Error ?? error?.error ?? "";
    return typeof msg === "string" && msg.toLowerCase().includes("recaptcha");
};

const initialState = {
    subscription: {
        plan: null,
        renewal: null,
        perMonth: null,
        perAnnual: null,
        billingPeriodUnit: null,
        card: null,
        currencyCode: null,
        status: null,
        subscriptionId: null,
        customerId: null,
        email: null,
        pausedAt: null,
        cancelledAt: null,
        trialEnd: null,
        currentTermEnd: null,
        channel: null,
        nextBillingAmount: null,
        upgradeTrial: null
    },
    invoices: [],
    currencyCode: null,
    upgradableSubscription: {},
    courses: [],
    isCTInvoiceDunning: false,
    paymentSources: []
}

export const billingDetails = createSlice({
    name: 'billingDetails',
    initialState,
    reducers: {
        setSubscription: (state, action) => {
            let { plan, renewal, perMonth, perAnnual, billingPeriodUnit, card, currencyCode, status, cancelReason, subscriptionId, customerId, email, pausedAt, cancelledAt, trialEnd, currentTermEnd, channel, nextBillingAmount, upgradeTrial } = action.payload;
            let subscription = { plan, renewal, perMonth, perAnnual, billingPeriodUnit, card, currencyCode, status, cancelReason, subscriptionId, customerId, email, pausedAt, cancelledAt, trialEnd, currentTermEnd, channel, nextBillingAmount, upgradeTrial };
            state.subscription = subscription;
        },
        setInvoices: (state, action) => {
            let { invoices, currencyCode, isCTInvoiceDunning } = action.payload;
            state.invoices = invoices;
            state.currencyCode = currencyCode;
            state.isCTInvoiceDunning = isCTInvoiceDunning;
        },
        setUpgradableSubscription: (state, action) => {
            let { plan, addons, charges, courses, currencyCode } = action.payload;
            let upgradableSubscription = { plan, addons: addons?.addons, charges, currencyCode };
            state.upgradableSubscription = upgradableSubscription;
            state.courses = courses;
        },
        setCourses: (state, action) => {
            // state.courses = action.payload;
        },
        setPaymentSources: (state, action) => {
            state.paymentSources = action.payload.paymentSources;
        }
    }
});

export const fetchSubscription = () => async (dispatch) => {
    const response = await api.get(baseURL, '/billing/subscription');
    dispatch(setSubscription(response));
    return response;
}

export const fetchInvoices = () => async (dispatch) => {
    const response = await api.get(baseURL, '/billing/invoices');
    dispatch(setInvoices(response));
    return response;
}

export const downloadInvoice = (invoiceId, type) => async (dispatch) => {
    const response = await api.get(baseURL, `/billing/invoices/${invoiceId}/download?type=${type}`);
    return response;
}

export const updateCard = (payload) => async (dispatch) => {
    const response = await api.post(baseURL, "/billing/card/update", payload);
    dispatch(setSubscription(response));
    return response;
}

export const fetchUpgradableSubscription = () => async (dispatch) => {
    const response = await api.get(baseURL, '/billing/upgradable');
    dispatch(setUpgradableSubscription(response));
    return response;
}

export const createSubscription = (payload, recaptchaToken) => async (dispatch) => {
    const response = await api.post(baseURL, '/checkout/upsert-subscription', payload, {
        'headers': { 'X-Captcha-Token': recaptchaToken }
    });
    dispatch(updateFlexTrack(response));
    return response;
}

// Mints a reCAPTCHA token and creates the subscription. reCAPTCHA is validated in middleware BEFORE
// the controller runs, so a reCAPTCHA failure means nothing was charged — it is safe to mint a fresh
// token and retry once. This recovers the common stale/expired-token case on a long-lived checkout
// page (e.g. after the PayPal popup + address entry) with no double-charge risk.
// Kicks off a reCAPTCHA assessment without awaiting it, so it can overlap a slow step earlier in the
// submit path. On the 3DS card flow the issuer authorization takes seconds, and there is no reason to
// pay for the captcha serially after it — nothing about the assessment depends on the card result.
// Pass the returned promise to createSubscriptionWithCaptcha.
//
// The bare catch exists only so an early failure isn't reported as an unhandled rejection while we
// are still awaiting authorization; awaiting the same promise later still observes it.
export const startRecaptchaAssessment = (action = "checkout") => {
    const pending = recaptchaAssessment(action);
    pending.catch(() => { });
    return pending;
}

export const createSubscriptionWithCaptcha = (payload, action = "checkout", pendingToken = null) => async (dispatch) => {
    let token;
    try {
        // Use an assessment the caller already started, when there is one, instead of starting a new
        // one here and running it after whatever the caller was waiting on.
        token = await (pendingToken ?? recaptchaAssessment(action));
    } catch (error) {
        // Starting early must leave us no worse off than not starting at all, so a failed pre-started
        // assessment falls back to a fresh request rather than failing the purchase.
        if (!pendingToken) {
            throw error;
        }
        token = await recaptchaAssessment(action);
    }
    try {
        return await dispatch(createSubscription(payload, token));
    } catch (error) {
        // A pre-started token has been alive since before authorization began, so on the 3DS path —
        // where the customer may have spent a minute on a challenge screen — it is likelier to have
        // aged out. This retry is what makes starting early safe.
        if (isRecaptchaError(error)) {
            const freshToken = await recaptchaAssessment(action);
            return await dispatch(createSubscription(payload, freshToken));
        }
        throw error;
    }
}

export const reactivateSubscription = () => async (dispatch) => {
    const response = await api.post(baseURL, '/billing/reactivate');
    dispatch(setSubscription(response));
    return response;
}

export const pauseSubscription = (payload) => async (dispatch) => {
    const response = await api.post(baseURL, '/billing/pause', payload);
    dispatch(setSubscription(response));
    return response;
}

export const resumeSubscription = () => async (dispatch) => {
    const response = await api.post(baseURL, '/billing/resume');
    dispatch(setSubscription(response));
    return response;
}

export const fetchCourses = () => async (dispatch) => {
    const response = await api.get(baseURL, '/billing/courses');
    dispatch(setCourses(response));
    return response;
}

export const listPaymentSource = () => async (dispatch) => {
    const response = await api.get(baseURL, '/billing/list-payment');
    dispatch(setPaymentSources({ 'paymentSources': response }));
    return response;
}

export const removeUpgradeSchedule = () => async (dispatch) => {
    const response = await api.post(baseURL, '/billing/remove-upgrade-schedule');
    dispatch(setSubscription(response));
    return response;
}

export const { setSubscription, setInvoices, setUpgradableSubscription, setCourses, setPaymentSources } = billingDetails.actions;

export default billingDetails.reducer;